import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { toast } from "sonner";
import { ChevronLeft, Copy, Share2, Users, CheckCircle2, Info, AlertTriangle, X, Upload, Loader2, Eye, EyeOff, Wallet, ExternalLink } from "lucide-react";

export default function Referrals() {
  const navigate = useNavigate();
  const safeBack = () => window.history.length > 2 ? navigate(-1) : navigate("/", { replace: true });
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [payoutRequested, setPayoutRequested] = useState(false);
  const [taxStatus, setTaxStatus] = useState(null); // {status, submitted_at, deny_reason}
  const [taxModalOpen, setTaxModalOpen] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);

  const handlePayoutRequest = async () => {
    setPayoutLoading(true);
    try {
      const res = await api.post("/referrals/request-payout");
      toast.success(res.data.message || "Payout transferred to your Stripe account!");
      setPayoutRequested(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit payout request.");
    } finally {
      setPayoutLoading(false);
    }
  };

  const handleConnectOnboard = async () => {
    setConnectLoading(true);
    try {
      const returnUrl = `${window.location.origin}/referrals`;
      const res = await api.post("/connect/onboard", {
        return_url: returnUrl,
        refresh_url: returnUrl,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Could not start Stripe onboarding. Please try again.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Stripe Connect setup.");
    } finally {
      setConnectLoading(false);
    }
  };

  const loadTaxStatus = async () => {
    try {
      const r = await api.get("/referrals/tax-info/status");
      setTaxStatus(r.data);
    } catch {
      setTaxStatus({ status: "none" });
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/referrals");
      setData(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403 && detail && typeof detail === "object" && detail.referral_unavailable) {
        navigate("/", { replace: true });
        return;
      }
      toast.error((typeof detail === "string" && detail) || e?.message || "Failed to load referrals");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadTaxStatus(); }, []);

  const copyLink = async () => {
    if (!data?.share_url) return;
    try {
      await navigator.clipboard.writeText(data.share_url);
      toast.success("Referral link copied");
    } catch {
      toast.error("Couldn't copy — long-press to copy manually");
    }
  };

  const nativeShare = async () => {
    if (!data?.share_url) return;
    const payload = {
      title: "Join ShiftQuote",
      text: "I've been using ShiftQuote to get quotes from local repair shops. Sign up using my link and earn me a commission as you grow.",
      url: data.share_url,
    };
    if (navigator.share) {
      try { await navigator.share(payload); } catch { /* user canceled */ }
    } else {
      await copyLink();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="text-center space-y-3">
          <Loader2 className="w-8 h-8 text-[#FF3B30] animate-spin mx-auto" />
          <div className="label-eyebrow text-zinc-500">SETTING UP YOUR REFERRAL CODE...</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="label-eyebrow text-zinc-500">COULD NOT LOAD REFERRAL DATA</div>
          <button onClick={load} className="btn-secondary text-sm">Try Again</button>
        </div>
      </div>
    );
  }

  const referralCount = data.referrals.length;
  const isApproved = taxStatus?.status === "approved";
  const codePaused = referralCount >= 2 && !isApproved;

  const connectStatus = data.stripe_connect_status || {};
  const isConnected = connectStatus.status === "active" && connectStatus.payouts_enabled;
  const isPending = connectStatus.status === "pending";
  const isNotStarted = connectStatus.status === "not_started" || !connectStatus.status;

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Navbar />
      <div className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => safeBack()} className="flex items-center gap-2 text-zinc-400 hover:text-white" data-testid="referrals-back-btn">
            <ChevronLeft className="w-5 h-5" /> Back
          </button>
          <div className="font-display text-sm tracking-widest text-zinc-500">REFERRAL EARNINGS</div>
          <div className="w-12" />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-8">
        {/* Earnings header */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-[#121214] border border-white/10 p-6" data-testid="referrals-lifetime">
            <div className="label-eyebrow text-zinc-500">LIFETIME EARNINGS</div>
            <div className="font-display text-4xl mt-2">
              ${data.total_lifetime_earnings.toFixed(2)}
            </div>
            <div className="text-xs text-zinc-600 mt-2">Already credited to your account.</div>
          </div>
          <div className="bg-[#121214] border border-white/10 p-6" data-testid="referrals-month">
            <div className="label-eyebrow text-zinc-500">THIS MONTH</div>
            <div className="font-display text-4xl mt-2">
              ${data.this_month_earnings.toFixed(2)}
            </div>
            <div className="text-xs text-zinc-600 mt-2">Resets on the 1st of every month.</div>
          </div>
          <div className="bg-[#121214] border border-[#34C759]/30 p-6" data-testid="referrals-mrr">
            <div className="label-eyebrow text-[#34C759]">MONTHLY RECURRING</div>
            <div className="font-display text-4xl mt-2">
              ${(data.current_monthly_recurring ?? 0).toFixed(2)}
            </div>
            <div className="text-xs text-zinc-600 mt-2">
              {data.active_count ?? 0} active · {data.inactive_count ?? 0} inactive
            </div>
          </div>
        </div>

        {/* Tax info notice banners */}
        {codePaused ? (
          <div
            className="bg-[#1A1300] border border-[#FFB800]/40 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            data-testid="tax-notice-limit"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-[#FFB800] mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-[#FFB800]">Your referral code is currently paused.</p>
                <p className="text-xs text-amber-300/70 mt-0.5">
                  Referring a 3rd shop without approval means the code won't count — the shop registers fine but you earn no credit. Submit your tax documents to unlock.
                </p>
              </div>
            </div>
            {taxStatus?.status === "pending" ? (
              <span className="text-xs font-bold text-amber-400 border border-amber-400/40 px-3 py-1.5 uppercase tracking-widest shrink-0 whitespace-nowrap">
                ⏳ Pending Review
              </span>
            ) : (
              <button
                onClick={() => setTaxModalOpen(true)}
                className="text-xs font-bold text-[#FFB800] border border-[#FFB800]/40 px-3 py-1.5 hover:bg-[#FFB800]/10 transition-colors uppercase tracking-widest shrink-0 whitespace-nowrap"
                data-testid="tax-notice-cta"
              >
                Submit Tax Info
              </button>
            )}
          </div>
        ) : (
          <div
            className="bg-[#0C1524] border border-[#2563EB]/25 px-4 py-3 flex items-start gap-2"
            data-testid="tax-notice-info"
          >
            <Info className="w-4 h-4 text-blue-400/60 mt-0.5 shrink-0" />
            <p className="text-xs text-blue-300/60 leading-relaxed">
              <span className="font-semibold text-blue-300/80">You can refer up to 2 shops for free.</span>{" "}
              A 3rd referral requires submitting your tax documents AND admin approval before your code will work again.
            </p>
          </div>
        )}

        {/* Tax status badges */}
        {taxStatus?.status === "approved" && (
          <div className="bg-[#0A1F0A] border border-[#34C759]/30 px-4 py-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-[#34C759] shrink-0" />
            <p className="text-sm text-[#34C759] font-semibold">Tax info verified — unlimited referrals unlocked</p>
          </div>
        )}
        {taxStatus?.status === "pending" && !codePaused && (
          <div className="bg-[#1A1300] border border-amber-500/30 px-4 py-3 flex items-center gap-2">
            <span className="text-sm text-amber-400 font-semibold">⏳ Tax info pending review</span>
          </div>
        )}
        {taxStatus?.status === "denied" && (
          <div className="bg-[#1A0A0A] border border-red-500/30 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start gap-2">
              <XCircleIcon className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm text-red-400 font-semibold">Submission denied{taxStatus.deny_reason ? `: ${taxStatus.deny_reason}` : ""}</p>
              </div>
            </div>
            <button
              onClick={() => setTaxModalOpen(true)}
              className="text-xs font-bold text-red-400 border border-red-400/40 px-3 py-1.5 hover:bg-red-400/10 transition-colors uppercase tracking-widest shrink-0 whitespace-nowrap"
            >
              Resubmit
            </button>
          </div>
        )}

        {/* TAX DOCUMENTS — always visible card */}
        <div className="bg-[#121214] border border-white/10 p-6" data-testid="tax-documents-card">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold text-zinc-500 uppercase tracking-widest">TAX DOCUMENTS</p>
            {taxStatus?.status === "pending" && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-amber-500/10 text-amber-400 px-2 py-0.5">
                🕐 Pending Review
              </span>
            )}
            {taxStatus?.status === "approved" && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-[#34C759]/10 text-[#34C759] px-2 py-0.5">
                ✓ Approved
              </span>
            )}
            {taxStatus?.status === "denied" && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-red-500/10 text-red-400 px-2 py-0.5">
                ✗ Denied
              </span>
            )}
          </div>

          {(!taxStatus || taxStatus.status === "none") && (
            <>
              <p className="text-sm text-zinc-400 mb-4">
                Submit your tax information now to unlock unlimited referrals. Required for 3+ referrals.
              </p>
              <button
                onClick={() => setTaxModalOpen(true)}
                className="w-full py-3 bg-[#FFB800] text-black font-display font-bold tracking-widest uppercase text-sm hover:bg-[#FFB800]/90 transition-colors"
                data-testid="tax-submit-cta"
              >
                Submit Tax Documents
              </button>
            </>
          )}

          {taxStatus?.status === "pending" && (
            <>
              <p className="text-sm text-zinc-400 mb-4">
                Your documents are under review. You'll be notified once approved.
              </p>
              <button
                disabled
                className="w-full py-3 bg-zinc-700 text-zinc-400 font-display font-bold tracking-widest uppercase text-sm cursor-not-allowed opacity-60"
              >
                Documents Submitted
              </button>
            </>
          )}

          {taxStatus?.status === "approved" && (
            <p className="text-sm text-zinc-400">
              Your tax documents have been approved. You can refer unlimited shops.
            </p>
          )}

          {taxStatus?.status === "denied" && (
            <>
              <p className="text-sm text-zinc-400 mb-4">
                Your documents were denied.{taxStatus.deny_reason ? ` ${taxStatus.deny_reason}.` : ""} Please resubmit.
              </p>
              <button
                onClick={() => setTaxModalOpen(true)}
                className="w-full py-3 bg-[#FFB800] text-black font-display font-bold tracking-widest uppercase text-sm hover:bg-[#FFB800]/90 transition-colors"
                data-testid="tax-resubmit-cta"
              >
                Resubmit Documents
              </button>
            </>
          )}
        </div>

        {/* Unique Code — gated when paused */}
        <div className={`bg-[#09090B] border p-6 mb-6 transition-all ${codePaused ? "border-white/5 opacity-50" : "border-white/10"}`}>
          <p className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-3">Your Referral Code</p>
          <div className="flex items-center gap-4">
            <span className={`font-mono text-3xl font-bold tracking-widest ${codePaused ? "text-zinc-600" : "text-[#FFB800]"}`}>{data?.code || "—"}</span>
            {codePaused ? (
              <span className="text-xs font-bold text-zinc-600 border border-white/5 px-3 py-1.5 uppercase tracking-widest cursor-not-allowed">
                Code Paused
              </span>
            ) : (
              <button
                onClick={() => { navigator.clipboard.writeText(data?.code || ""); toast.success("Code copied!"); }}
                className="text-xs font-bold text-zinc-400 border border-white/10 px-3 py-1.5 hover:border-white/30 hover:text-white transition-colors uppercase tracking-widest"
              >
                Copy
              </button>
            )}
          </div>
          {codePaused && (
            <p className="text-xs text-zinc-600 mt-3">Referral code paused — pending tax document approval</p>
          )}
          {!codePaused && (
            <p className="text-xs text-zinc-600 mt-3">Share this code with shops. They enter it during sign-up and you earn credit automatically.</p>
          )}
        </div>

        {/* Referral link */}
        <div className={`bg-[#121214] border border-white/10 p-6 space-y-3 ${codePaused ? "opacity-50 pointer-events-none" : ""}`}>
          <div className="label-eyebrow text-zinc-500">YOUR REFERRAL LINK</div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex-1 min-w-0 bg-[#09090B] border border-white/10 px-3 py-2 font-mono text-xs sm:text-sm break-all" data-testid="referrals-link">
              {data.share_url}
            </div>
            {!codePaused && (
              <>
                <button onClick={copyLink} className="btn-secondary flex items-center gap-2" data-testid="referrals-copy-btn">
                  <Copy className="w-4 h-4" /> Copy
                </button>
                <button onClick={nativeShare} className="px-4 py-2 bg-[#FF3B30] text-white font-display tracking-wide flex items-center gap-2" data-testid="referrals-share-btn">
                  <Share2 className="w-4 h-4" /> Share
                </button>
              </>
            )}
          </div>
          {!codePaused && (
            <div className="text-sm text-zinc-400">
              Your code: <span className="font-mono text-white">{data.code}</span>
            </div>
          )}
        </div>

        {/* Commission rates */}
        <div className="bg-[#121214] border border-white/10 p-6">
          <div className="label-eyebrow text-zinc-500">COMMISSION RATES</div>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <div className="text-sm text-zinc-500">Pro shop</div>
              <div className="font-display text-3xl text-[#FFB800]">${data.commissions.pro_monthly_usd}<span className="text-sm text-zinc-500">/mo</span></div>
            </div>
            <div>
              <div className="text-sm text-zinc-500">Premium shop</div>
              <div className="font-display text-3xl text-[#34C759]">${data.commissions.premium_monthly_usd}<span className="text-sm text-zinc-500">/mo</span></div>
            </div>
          </div>
          <div className="text-xs text-zinc-600 mt-4">Commissions are earned monthly while the referred shop stays subscribed. If they cancel, commissions end permanently and do not resume on resubscribe.</div>
        </div>

        {/* Active referrals */}
        <div>
          <div className="label-eyebrow text-zinc-500 mb-3 flex items-center gap-2 flex-wrap gap-y-1">
            <Users className="w-3.5 h-3.5" /> YOUR REFERRALS · {data.referrals.length}
            {data.referrals.filter(r => r.status === "active").length > 0 && (
              <span className="bg-[#34C759]/10 text-[#34C759] text-[10px] font-mono px-1.5 py-0.5">{data.referrals.filter(r => r.status === "active").length} ACTIVE</span>
            )}
            {data.referrals.filter(r => r.status === "pending").length > 0 && (
              <span className="bg-[#FFB800]/10 text-[#FFB800] text-[10px] font-mono px-1.5 py-0.5">{data.referrals.filter(r => r.status === "pending").length} PENDING</span>
            )}
          </div>
          {data.referrals.length === 0 ? (
            <div className="bg-[#121214] border border-dashed border-white/10 px-6 py-10 text-center text-zinc-500" data-testid="referrals-empty">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <div className="text-white font-display text-lg">No referrals yet</div>
              <div className="text-sm mt-1">Share your link above to start earning.</div>
            </div>
          ) : (
            <div className="bg-[#121214] border border-white/10 overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-4 py-3 text-xs text-zinc-500 border-b border-white/5 font-mono tracking-wide">
                <div className="col-span-5">SHOP</div>
                <div className="col-span-3">PLAN</div>
                <div className="col-span-2">STATUS</div>
                <div className="col-span-2 text-right">MONTHLY</div>
              </div>
              {data.referrals.map((r, i) => {
                const isActive = r.status === "active";
                return (
                <div key={i} className="grid grid-cols-12 gap-2 px-4 py-3 text-sm border-b border-white/5 last:border-b-0" data-testid={`referral-row-${i}`}>
                  <div className="col-span-5 truncate">{r.shop_name}</div>
                  <div className="col-span-3 capitalize text-zinc-400">{r.tier}</div>
                  <div className="col-span-2">
                    {(() => {
                      if (isActive) return <span className="px-1.5 py-0.5 text-[10px] font-mono bg-[#34C759]/10 text-[#34C759]">ACTIVE</span>;
                      if (r.status === "pending") return (
                        <>
                          <span className="px-1.5 py-0.5 text-[10px] font-mono bg-[#FFB800]/10 text-[#FFB800]">PENDING</span>
                          <div className="text-[10px] text-zinc-600 mt-0.5">Awaiting subscription</div>
                        </>
                      );
                      return (
                        <>
                          <span className="px-1.5 py-0.5 text-[10px] font-mono bg-zinc-700/40 text-zinc-500">INACTIVE</span>
                          <div className="text-[10px] text-zinc-600 mt-0.5">Subscription inactive</div>
                        </>
                      );
                    })()}
                  </div>
                  <div className={`col-span-2 text-right font-mono ${isActive ? "" : "text-zinc-600 line-through"}`}>${r.monthly_earning.toFixed(2)}</div>
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Stripe Connect Payout Section */}
        <div className="border border-white/10 bg-[#121214] p-6" data-testid="payout-connect-card">
          <div className="flex items-center gap-3 mb-4">
            <Wallet className="w-5 h-5 text-[#635BFF]" strokeWidth={1.8} />
            <div className="label-eyebrow text-zinc-400">PAYOUT ACCOUNT</div>
          </div>

          {/* Not connected */}
          {isNotStarted && (
            <div className="space-y-4">
              <p className="text-sm text-zinc-400">
                Connect your Stripe account to receive referral payouts directly. Setup takes about 2 minutes.
              </p>
              <button
                onClick={handleConnectOnboard}
                disabled={connectLoading}
                className="w-full py-3 bg-[#635BFF] text-white font-display font-bold tracking-widest uppercase text-sm hover:bg-[#635BFF]/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="connect-onboard-btn"
              >
                {connectLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Setting up...</>
                ) : (
                  <><ExternalLink className="w-4 h-4" /> Connect Stripe Account</>
                )}
              </button>
            </div>
          )}

          {/* Pending / incomplete onboarding */}
          {isPending && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-amber-500/10 text-amber-400 px-2 py-0.5">
                  ⏳ Setup Incomplete
                </span>
              </div>
              <p className="text-sm text-zinc-400">
                Your Stripe account is connected but onboarding is incomplete. Finish setup to enable payouts.
              </p>
              <button
                onClick={handleConnectOnboard}
                disabled={connectLoading}
                className="w-full py-3 bg-[#635BFF] text-white font-display font-bold tracking-widest uppercase text-sm hover:bg-[#635BFF]/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {connectLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Loading...</>
                ) : (
                  <><ExternalLink className="w-4 h-4" /> Complete Stripe Setup</>
                )}
              </button>
            </div>
          )}

          {/* Connected and active */}
          {isConnected && (
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#34C759] shrink-0" />
              <div>
                <p className="text-sm font-semibold text-[#34C759]">Stripe Connected</p>
                <p className="text-xs text-zinc-500 mt-0.5">Payouts will be sent directly to your bank via Stripe.</p>
              </div>
            </div>
          )}
        </div>

        {/* Request Payout Section */}
        <div className="border border-white/10 p-6" data-testid="payout-request-card">
          <div className="font-display text-lg mb-1">Request Payout</div>
          <div className="text-xs text-zinc-500 mb-4">Minimum ${data.payout?.minimum_usd?.toFixed(2) ?? "25.00"} · 30-day earnings hold · Instant via Stripe</div>

          {payoutRequested ? (
            <div className="bg-green-500/10 border border-green-500/20 p-4">
              <p className="text-sm font-bold text-green-400">✓ Payout Transferred</p>
              <p className="text-xs text-zinc-400 mt-1">Funds have been sent to your Stripe account. They typically arrive in your bank within 1-2 business days.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-2xl font-bold font-display text-white">${(data.payout?.available ?? 0).toFixed(2)}</p>
                  <p className="text-xs text-zinc-500">Available to request</p>
                </div>
                <button
                  onClick={handlePayoutRequest}
                  disabled={payoutLoading || !isConnected || (data.payout?.available ?? 0) < (data.payout?.minimum_usd ?? 25)}
                  className="btn-primary text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {payoutLoading ? "Transferring..." : "Request Payout"}
                </button>
              </div>
              {(data.payout?.available ?? 0) < (data.payout?.minimum_usd ?? 25) && (
                <p className="text-xs text-zinc-600">
                  You need ${((data.payout?.minimum_usd ?? 25) - (data.payout?.available ?? 0)).toFixed(2)} more to reach the minimum payout threshold.
                </p>
              )}
              {!isConnected && (
                <p className="text-xs text-amber-500 mt-2">⚠ Connect your Stripe account above before requesting a payout.</p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Tax Info Modal */}
      {taxModalOpen && (
        <TaxInfoModal
          onClose={() => setTaxModalOpen(false)}
          onSuccess={() => {
            setTaxModalOpen(false);
            loadTaxStatus();
            toast.success("Tax info submitted — pending admin review");
          }}
        />
      )}
    </div>
  );
}

function XCircleIcon({ className }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/>
    </svg>
  );
}

function TaxInfoModal({ onClose, onSuccess }) {
  const [legalName, setLegalName] = useState("");
  const [taxIdType, setTaxIdType] = useState("ssn");
  const [taxId, setTaxId] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [govIdFile, setGovIdFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showTaxId, setShowTaxId] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const canSubmit = legalName.trim() && taxId.trim().length >= 4 && street.trim() && city.trim() && state.trim().length === 2 && zip.trim().length >= 5 && govIdFile && !uploading && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // Upload gov ID photo first
      setUploading(true);
      const fd = new FormData();
      fd.append("file", govIdFile);
      const uploadRes = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const govIdPhotoUrl = uploadRes.data?.url || uploadRes.data?.path || "";
      setUploading(false);

      if (!govIdPhotoUrl) {
        toast.error("Failed to upload government ID photo");
        setSubmitting(false);
        return;
      }

      // Submit tax info
      await api.post("/referrals/tax-info", {
        legal_name: legalName.trim(),
        tax_id_type: taxIdType,
        tax_id: taxId.trim(),
        mailing_address: { street: street.trim(), city: city.trim(), state: state.trim().toUpperCase(), zip: zip.trim() },
        gov_id_photo_url: govIdPhotoUrl,
      });
      onSuccess();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit tax info");
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" onClick={onClose}>
      <div className="bg-[#121214] border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 relative" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white">
          <X className="w-5 h-5" />
        </button>
        <h2 className="font-display text-xl mb-1">Submit Tax Information</h2>
        <p className="text-xs text-zinc-500 mb-6">Required to unlock unlimited referrals. We only store the last 4 digits of your tax ID.</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-bold text-zinc-400 uppercase tracking-widest block mb-1">Legal Full Name</label>
            <input
              type="text"
              value={legalName}
              onChange={e => setLegalName(e.target.value)}
              className="w-full bg-[#09090B] border border-white/10 px-3 py-2 text-sm text-white placeholder-zinc-600 focus:border-white/30 focus:outline-none"
              placeholder="John M. Doe"
              required
            />
          </div>

          <div>
            <label className="text-xs font-bold text-zinc-400 uppercase tracking-widest block mb-1">Tax ID Type</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setTaxIdType("ssn")}
                className={`flex-1 px-3 py-2 text-sm font-bold uppercase tracking-widest border transition-colors ${taxIdType === "ssn" ? "border-[#FFB800] text-[#FFB800] bg-[#FFB800]/10" : "border-white/10 text-zinc-500 hover:border-white/30"}`}
              >
                SSN
              </button>
              <button
                type="button"
                onClick={() => setTaxIdType("ein")}
                className={`flex-1 px-3 py-2 text-sm font-bold uppercase tracking-widest border transition-colors ${taxIdType === "ein" ? "border-[#FFB800] text-[#FFB800] bg-[#FFB800]/10" : "border-white/10 text-zinc-500 hover:border-white/30"}`}
              >
                EIN
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-zinc-400 uppercase tracking-widest block mb-1">
              {taxIdType === "ssn" ? "Social Security Number" : "Employer Identification Number"}
            </label>
            <div className="relative">
              <input
                type={showTaxId ? "text" : "password"}
                value={taxId}
                onChange={e => setTaxId(e.target.value)}
                className="w-full bg-[#09090B] border border-white/10 px-3 py-2 pr-10 text-sm text-white placeholder-zinc-600 focus:border-white/30 focus:outline-none"
                placeholder={taxIdType === "ssn" ? "Enter your SSN" : "Enter your EIN"}
                required
              />
              <button
                type="button"
                onClick={() => setShowTaxId(v => !v)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-zinc-500 hover:text-zinc-300 transition-colors"
                aria-label={showTaxId ? "Hide tax ID" : "Show tax ID"}
              >
                {showTaxId ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-[10px] text-zinc-600 mt-1">We only store the last 4 digits</p>
          </div>

          <div>
            <label className="text-xs font-bold text-zinc-400 uppercase tracking-widest block mb-1">Street Address</label>
            <input
              type="text"
              value={street}
              onChange={e => setStreet(e.target.value)}
              className="w-full bg-[#09090B] border border-white/10 px-3 py-2 text-sm text-white placeholder-zinc-600 focus:border-white/30 focus:outline-none"
              placeholder="123 Main St, Apt 4"
              required
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-bold text-zinc-400 uppercase tracking-widest block mb-1">City</label>
              <input
                type="text"
                value={city}
                onChange={e => setCity(e.target.value)}
                className="w-full bg-[#09090B] border border-white/10 px-3 py-2 text-sm text-white placeholder-zinc-600 focus:border-white/30 focus:outline-none"
                placeholder="City"
                required
              />
            </div>
            <div>
              <label className="text-xs font-bold text-zinc-400 uppercase tracking-widest block mb-1">State</label>
              <input
                type="text"
                value={state}
                onChange={e => setState(e.target.value)}
                maxLength={2}
                className="w-full bg-[#09090B] border border-white/10 px-3 py-2 text-sm text-white placeholder-zinc-600 focus:border-white/30 focus:outline-none uppercase"
                placeholder="CA"
                required
              />
            </div>
            <div>
              <label className="text-xs font-bold text-zinc-400 uppercase tracking-widest block mb-1">ZIP</label>
              <input
                type="text"
                value={zip}
                onChange={e => setZip(e.target.value)}
                className="w-full bg-[#09090B] border border-white/10 px-3 py-2 text-sm text-white placeholder-zinc-600 focus:border-white/30 focus:outline-none"
                placeholder="92694"
                required
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-zinc-400 uppercase tracking-widest block mb-1">Government ID Photo</label>
            <p className="text-[10px] text-zinc-600 mb-2">Upload a photo of your government-issued ID (driver's license, passport, etc.)</p>
            <div
              onClick={() => fileRef.current?.click()}
              className={`border border-dashed px-4 py-4 text-center cursor-pointer transition-colors ${govIdFile ? "border-[#34C759]/40 bg-[#34C759]/5" : "border-white/10 hover:border-white/30"}`}
            >
              {govIdFile ? (
                <div className="flex items-center justify-center gap-2 text-sm text-[#34C759]">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="truncate max-w-[200px]">{govIdFile.name}</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2 text-sm text-zinc-500">
                  <Upload className="w-4 h-4" />
                  <span>Click to upload ID photo</span>
                </div>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={e => { if (e.target.files?.[0]) setGovIdFile(e.target.files[0]); }}
            />
            {!govIdFile && <p className="text-[10px] text-red-400/70 mt-1">* Required</p>}
          </div>

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full py-3 bg-[#FFB800] text-black font-display font-bold tracking-widest uppercase text-sm disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#FFB800]/90 transition-colors flex items-center justify-center gap-2"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : "Submit Tax Information"}
          </button>
        </form>
      </div>
    </div>
  );
}
